"use client";
import { useRouter } from "next/navigation";
import { useAuth } from "@/components/AuthProvider";
import { DirectoryProvider } from "@/components/directory";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/radix-tabs';
import { Button } from '@/components/ui/button';
import { TableIcon, MapPinned, Handshake, LineChart, FlaskConical, ClipboardList, Building2, Star, ExternalLink, Search, X, Filter, Wand2 } from 'lucide-react';
import { DirectoryTable, useDirectory } from '@/components/directory';
import { AIRTABLE_TABLES } from '@/lib/airtable/config';
import ClientOnly from '@/components/ClientOnly';
import React, { useState, useEffect, useMemo } from 'react';
import { LoadingPattern } from "@/components/ui/pattern-viewer";
import { useDynamicAirtableRecords } from '@/lib/airtable/client-hooks';
import { parseAttachments } from '@/lib/airtable/transform';
import Link from 'next/link';

const TABLE_KEYS = [
  'PROVIDERS',
  'PRODUCTS',
  'SERVICES',
  'TECHNOLOGY',
  'PLATFORMS',
];

function DirectoryTabs() {
  const {
    activeTable,
    setActiveTable,
  } = useDirectory();

  React.useEffect(() => {
    if (!activeTable || !TABLE_KEYS.includes(activeTable)) {
      setActiveTable('PROVIDERS');
    }
  }, [activeTable, setActiveTable]);

  return (
    <div className="space-y-4">
      <Tabs value="table" className="w-full">
        <TabsList className="flex gap-2 mb-4">
          <TabsTrigger value="table">
            <TableIcon className="mr-2 h-4 w-4" /> Table
          </TabsTrigger>
        </TabsList>
        <div className="flex gap-2 mb-4">
          {TABLE_KEYS.map((key) => {
            const table = AIRTABLE_TABLES[key];
            if (!table) {
              console.warn(`AIRTABLE_TABLES missing key: ${key}`);
              return null;
            }
            return (
              <Button
                key={key}
                variant={activeTable === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTable(key)}
              >
                {table.name}
              </Button>
            );
          })}
        </div>
        <TabsContent value="table">
          <DirectoryTable tableName={activeTable} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Featured Platforms Component
function FeaturedPlatforms() {
  const { data: platformsData, isLoading, isError } = useDynamicAirtableRecords('PLATFORMS');
  
  // Helper function to extract Platform fields
  function extractPlatformFields(platform: any) {
    if (!platform) return {};
    
    const attachments = (platform['Logo'] && Array.isArray(platform['Logo']))
      ? parseAttachments(platform['Logo']) ?? []
      : [];
    const logo = attachments.length > 0 ? attachments[0] : null;
    const logoSrc = logo && typeof logo === 'object' ? (logo.thumbnailLarge ?? logo.url ?? '') : '';
    
    // Get provider logo as fallback
    // Extract Provider Logo from lookup field
    const providerLogoSrc = (() => {
      const providerLogoField = platform['Provider Logo'];
      if (!providerLogoField) return '';
      
      // Handle lookup field structure - it contains attachment data from linked provider records
      if (Array.isArray(providerLogoField)) {
        // Find the first provider logo attachment
        for (const logoData of providerLogoField) {
          if (logoData && typeof logoData === 'object') {
            // The lookup field contains attachment objects directly
            if (logoData.thumbnailLarge) return logoData.thumbnailLarge;
            if (logoData.url) return logoData.url;
          }
        }
      }
      
      return '';
    })();
    
    // Get image with fallback system: Instrument Image → Assay Kit Image → Assay Panel Image → Software Image
    const getImageSrc = (fieldName: string) => {
      const attachments = (platform[fieldName] && Array.isArray(platform[fieldName]))
        ? parseAttachments(platform[fieldName]) ?? []
        : [];
      const image = attachments.length > 0 ? attachments[0] : null;
      return image && typeof image === 'object' ? (image.thumbnailLarge ?? image.url ?? '') : '';
    };

    const instrumentImageSrc = getImageSrc('Instrument Image') || 
                              getImageSrc('Assay Kit Image') || 
                              getImageSrc('Assay Panel Image') || 
                              getImageSrc('Software Image') || '';
    
    // Use the best available image
    const finalLogoSrc = logoSrc || providerLogoSrc || instrumentImageSrc;
    
    const name = platform['Platform Name'] || platform['Name'] || platform['name'] || '';
    const description = platform['Platform Description'] || platform['Description'] || platform['description'] || '';
    const website = platform['Website'] || platform['website'] || '';
    const location = platform['Location'] || platform['location'] || '';
    const slug = platform['Slug'] || platform['slug'] || '';
    
    return { logoSrc: finalLogoSrc, name, description, website, location, slug };
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow-lg p-6 h-80 animate-pulse">
            <div className="h-24 bg-neutral-200 rounded mb-4"></div>
            <div className="h-6 bg-neutral-200 rounded mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (isError || !platformsData || !Array.isArray(platformsData)) {
    return <div className="text-center text-neutral-500">Error loading featured platforms.</div>;
  }

  // Filter out 'Platform Agnostic' and take only the first 3 platforms
  const featuredPlatforms = platformsData
    .filter((platform: any) => {
      const { name } = extractPlatformFields(platform);
      return name !== 'Platform Agnostic' && name && name.trim() !== '';
    })
    .slice(0, 3);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {featuredPlatforms.map((platform: any, idx: number) => {
        const { logoSrc, name, description, website, location, slug } = extractPlatformFields(platform);
        
        // If we have a slug, make the entire card clickable
        if (slug) {
          return (
            <Link
              key={platform.id || idx}
              href={`/platforms/${slug}`}
              className="block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
              aria-label={`View details for ${name}`}
            >
              <div className="bg-white rounded-lg shadow-lg p-6 h-80 flex flex-col hover:shadow-xl transition-shadow duration-200 cursor-pointer">
                {/* Logo Section - Fixed Height */}
                <div className="h-24 flex items-center justify-center mb-4">
                  {logoSrc ? (
                    <img 
                      src={logoSrc} 
                      alt={name} 
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-neutral-100 rounded-lg flex items-center justify-center">
                      <FlaskConical className="w-8 h-8 text-neutral-400" />
                    </div>
                  )}
                </div>
                
                {/* Content Section - Flexible but with consistent spacing */}
                <div className="flex-1 flex flex-col">
                  <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
                    {name}
                  </h3>
                  
                  {location && (
                    <p className="text-sm text-neutral-600 mb-2 italic">
                      {location}
                    </p>
                  )}
                  
                  {description && (
                    <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
                      {description}
                    </p>
                  )}
                  
                  {/* Button Section - Fixed at bottom */}
                  <div className="mt-auto">
                  </div>
                </div>
              </div>
            </Link>
          );
        }

        // If no slug, render as a regular div with website link
        return (
          <div key={platform.id || idx} className="bg-white rounded-lg shadow-lg p-6 h-80 flex flex-col">
            {/* Logo Section - Fixed Height */}
            <div className="h-24 flex items-center justify-center mb-4">
              {logoSrc ? (
                <img 
                  src={logoSrc} 
                  alt={name} 
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <div className="w-16 h-16 bg-neutral-100 rounded-lg flex items-center justify-center">
                  <FlaskConical className="w-8 h-8 text-neutral-400" />
                </div>
              )}
            </div>
            
            {/* Content Section - Flexible but with consistent spacing */}
            <div className="flex-1 flex flex-col">
              <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
                {name}
              </h3>
              
              {location && (
                <p className="text-sm text-neutral-600 mb-2 italic">
                  {location}
                </p>
              )}
              
              {description && (
                <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
                  {description}
                </p>
              )}
              
              {/* Button Section - Fixed at bottom */}
              <div className="mt-auto">
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Featured CROs Component
function FeaturedCROs() {
  const { data: crosData, isLoading, isError } = useDynamicAirtableRecords('CROS');
  
  // Helper function to extract CRO fields
  function extractCROFields(cro: any) {
    if (!cro) return {};
    
    const attachments = (cro['Logo'] && Array.isArray(cro['Logo']))
      ? parseAttachments(cro['Logo']) ?? []
      : [];
    const logo = attachments.length > 0 ? attachments[0] : null;
    const logoSrc = logo && typeof logo === 'object' ? (logo.thumbnailLarge ?? logo.url ?? '') : '';
    
    const name = cro['Provider'] || cro['CRO'] || cro['Name'] || cro['name'] || '';
    const description = cro['Description'] || cro['description'] || '';
    const website = cro['Website'] || cro['website'] || '';
    const location = cro['Location'] || cro['location'] || '';
    const slug = cro['Slug'] || cro['slug'] || '';
    
    return { logoSrc, name, description, website, location, slug };
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow-lg p-6 h-80 animate-pulse">
            <div className="h-24 bg-neutral-200 rounded mb-4"></div>
            <div className="h-6 bg-neutral-200 rounded mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (isError || !crosData || !Array.isArray(crosData)) {
    return <div className="text-center text-neutral-500">Error loading featured CROs.</div>;
  }

  // Take only the first 3 CROs
  const featuredCROs = crosData.slice(0, 3);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {featuredCROs.map((cro: any, idx: number) => {
        const { logoSrc, name, description, website, location, slug } = extractCROFields(cro);
        
        // If we have a slug, make the entire card clickable
        if (slug) {
          return (
            <Link
              key={cro.id || idx}
              href={`/cros/${slug}`}
              className="block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
              aria-label={`View details for ${name}`}
            >
              <div className="bg-white rounded-lg shadow-lg p-6 h-80 flex flex-col hover:shadow-xl transition-shadow duration-200 cursor-pointer">
                {/* Logo Section - Fixed Height */}
                <div className="h-24 flex items-center justify-center mb-4">
                  {logoSrc ? (
                    <img 
                      src={logoSrc} 
                      alt={name} 
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-neutral-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-neutral-400" />
                    </div>
                  )}
                </div>
                
                {/* Content Section - Flexible but with consistent spacing */}
                <div className="flex-1 flex flex-col">
                  <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
                    {name}
                  </h3>
                  
                  {location && (
                    <p className="text-sm text-neutral-600 mb-2 italic">
                      {location}
                    </p>
                  )}
                  
                  {description && (
                    <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
                      {description}
                    </p>
                  )}
                  
                  {/* Button Section - Fixed at bottom */}
                  <div className="mt-auto">
                  </div>
                </div>
              </div>
            </Link>
          );
        }

        // If no slug, render as a regular div with website link
        return (
          <div key={cro.id || idx} className="bg-white rounded-lg shadow-lg p-6 h-80 flex flex-col">
            {/* Logo Section - Fixed Height */}
            <div className="h-24 flex items-center justify-center mb-4">
              {logoSrc ? (
                <img 
                  src={logoSrc} 
                  alt={name} 
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <div className="w-16 h-16 bg-neutral-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-neutral-400" />
                </div>
              )}
            </div>
            
            {/* Content Section - Flexible but with consistent spacing */}
            <div className="flex-1 flex flex-col">
              <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
                {name}
              </h3>
              
              {location && (
                <p className="text-sm text-neutral-600 mb-2 italic">
                  {location}
                </p>
              )}
              
              {description && (
                <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
                  {description}
                </p>
              )}
              
              {/* Button Section - Fixed at bottom */}
              <div className="mt-auto">
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Guided Research Wizard Component
function GuidedResearchWizard() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSamples, setSelectedSamples] = useState<string[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<string[]>([]);
  const [selectedMeasurements, setSelectedMeasurements] = useState<string[]>([]);
  const [selectedSlideResolution, setSelectedSlideResolution] = useState<string[]>([]);
  const [selectedMarkers, setSelectedMarkers] = useState<string[]>([]);
  const [selectedResolution, setSelectedResolution] = useState<string[]>([]);
  const [selectedThroughput, setSelectedThroughput] = useState<string[]>([]);

  const totalSteps = 7;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Navigate to comparison page with wizard selections as URL parameters
      const params = new URLSearchParams();
      if (selectedSamples.length > 0) params.set('samples', selectedSamples.join(','));
      if (selectedAnalysis.length > 0) params.set('analysis', selectedAnalysis.join(','));
      if (selectedMeasurements.length > 0) params.set('measurements', selectedMeasurements.join(','));
      if (selectedSlideResolution.length > 0) params.set('slideResolution', selectedSlideResolution.join(','));
      if (selectedMarkers.length > 0) params.set('markers', selectedMarkers.join(','));
      if (selectedResolution.length > 0) params.set('resolution', selectedResolution.join(','));
      if (selectedThroughput.length > 0) params.set('throughput', selectedThroughput.join(','));
      
      const queryString = params.toString();
      const url = queryString ? `/comparison?${queryString}` : '/comparison';
      router.push(url);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleSelection = (value: string, selectedArray: string[], setSelectedArray: (arr: string[]) => void) => {
    if (selectedArray.includes(value)) {
      setSelectedArray(selectedArray.filter(item => item !== value));
    } else {
      setSelectedArray([...selectedArray, value]);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-primary-100 text-primary-700 font-bold text-2xl w-16 h-16 rounded-lg flex items-center justify-center">
                1
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-700 mb-2">
                  What kind of samples are you using?
                </h3>
                <p className="text-neutral-600 italic">(select all that apply)</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {['Cell Suspension', 'Nuclei Suspension', 'FFPE Tissue', 'Fresh Frozen Tissue', 'Fixed Frozen Tissue'].map((sample) => (
                <button
                  key={sample}
                  onClick={() => toggleSelection(sample, selectedSamples, setSelectedSamples)}
                  className={`px-8 py-4 rounded-lg font-semibold transition-all duration-200 min-w-[200px] min-h-[80px] border-2 ${
                    selectedSamples.includes(sample)
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-300'
                  }`}
                >
                  {sample}
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-primary-100 text-primary-700 font-bold text-2xl w-16 h-16 rounded-lg flex items-center justify-center">
                2
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-700 mb-2">
                  What kind of analysis are you planning to perform?
                </h3>
                <p className="text-neutral-600 italic">(select all that apply)</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {['Spatial Relationships', 'Single-Cell Clustering', 'Whole Transcriptome', 'Spatial Phenotyping', 'Bulk Sequencing'].map((analysis) => (
                <button
                  key={analysis}
                  onClick={() => toggleSelection(analysis, selectedAnalysis, setSelectedAnalysis)}
                  className={`px-8 py-4 rounded-lg font-semibold transition-all duration-200 min-w-[200px] min-h-[80px] border-2 ${
                    selectedAnalysis.includes(analysis)
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-300'
                  }`}
                >
                  {analysis}
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-primary-100 text-primary-700 font-bold text-2xl w-16 h-16 rounded-lg flex items-center justify-center">
                3
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-700 mb-2">
                  What do you want to measure spatially?
                </h3>
                <p className="text-neutral-600 italic">(select all that apply)</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {['Protein', 'RNA', 'DNA'].map((measurement) => (
                <button
                  key={measurement}
                  onClick={() => toggleSelection(measurement, selectedMeasurements, setSelectedMeasurements)}
                  className={`px-8 py-4 rounded-lg font-semibold transition-all duration-200 min-w-[200px] min-h-[80px] border-2 ${
                    selectedMeasurements.includes(measurement)
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-300'
                  }`}
                >
                  {measurement}
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-primary-100 text-primary-700 font-bold text-2xl w-16 h-16 rounded-lg flex items-center justify-center">
                4
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-700 mb-2">
                  What level of slide resolution do you need?
                </h3>
                <p className="text-neutral-600 italic">(select all that apply)</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {['Region of Interest (ROI)', 'Whole Slide', 'Platform Dependent'].map((slideRes) => (
                <button
                  key={slideRes}
                  onClick={() => toggleSelection(slideRes, selectedSlideResolution, setSelectedSlideResolution)}
                  className={`px-8 py-4 rounded-lg font-semibold transition-all duration-200 min-w-[200px] min-h-[80px] border-2 ${
                    selectedSlideResolution.includes(slideRes)
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-300'
                  }`}
                >
                  {slideRes}
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-primary-100 text-primary-700 font-bold text-2xl w-16 h-16 rounded-lg flex items-center justify-center">
                5
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-700 mb-2">
                  How many markers do you want to detect?
                </h3>
                <p className="text-neutral-600 italic">(select all that apply)</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {[
                { key: 'Low Plex', label: 'Low Plex', description: '<10 markers' },
                { key: 'Mid Plex', label: 'Mid Plex', description: '10-50 markers' },
                { key: 'High Plex', label: 'High Plex', description: '50+ markers' }
              ].map((marker) => (
                <button
                  key={marker.key}
                  onClick={() => toggleSelection(marker.key, selectedMarkers, setSelectedMarkers)}
                  className={`px-8 py-4 rounded-lg font-semibold transition-all duration-200 flex flex-col items-center justify-center min-w-[200px] min-h-[80px] border-2 ${
                    selectedMarkers.includes(marker.key)
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-300'
                  }`}
                >
                  <span className="text-lg">{marker.label}</span>
                  <span className="text-neutral-500 text-sm mt-1">{marker.description}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-primary-100 text-primary-700 font-bold text-2xl w-16 h-16 rounded-lg flex items-center justify-center">
                6
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-700 mb-2">
                  What is your required cellular resolution?
                </h3>
                <p className="text-neutral-600 italic">(select all that apply)</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {['Single Cell', 'Subcellular', 'Whole Tissue', 'Single Molecule'].map((resolution) => (
                <button
                  key={resolution}
                  onClick={() => toggleSelection(resolution, selectedResolution, setSelectedResolution)}
                  className={`px-8 py-4 rounded-lg font-semibold transition-all duration-200 min-w-[200px] min-h-[80px] border-2 ${
                    selectedResolution.includes(resolution)
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-300'
                  }`}
                >
                  {resolution}
                </button>
              ))}
            </div>
          </div>
        );

      case 7:
        return (
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-primary-100 text-primary-700 font-bold text-2xl w-16 h-16 rounded-lg flex items-center justify-center">
                7
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-700 mb-2">
                  What level of throughput do you need?
                </h3>
                <p className="text-neutral-600 italic">(select all that apply)</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'Low', label: 'Low', description: 'Low slide/sample capacity or slow turnaround; high-plex tradeoff' },
                { key: 'Moderate', label: 'Moderate', description: 'Lower sample numbers, longer run times, or semi-manual processes' },
                { key: 'High', label: 'High', description: 'High sample capacity with short run times and automation' },
                { key: 'Very High', label: 'Very High', description: 'High sample and cell capacity; fast time to results; scalable and automated' }
              ].map((throughput) => (
                <button
                  key={throughput.key}
                  onClick={() => toggleSelection(throughput.key, selectedThroughput, setSelectedThroughput)}
                  className={`px-6 py-4 rounded-lg font-semibold transition-all duration-200 flex flex-col items-start justify-center min-h-[80px] text-left border-2 ${
                    selectedThroughput.includes(throughput.key)
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-neutral-50 border-neutral-200 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-300'
                  }`}
                >
                  <span className="text-lg">{throughput.label}</span>
                  <span className="text-neutral-500 text-sm mt-1">{throughput.description}</span>
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };





  return (
    <section className="w-full py-16 px-4 md:px-12 bg-white rounded-none flex flex-col items-center justify-center">
      <div className="max-w-7xl w-full mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-2">
            <Wand2 className="text-accent-600" size={32} />
            <h2 className="text-3xl md:text-4xl font-bold text-primary-700">
              Comparison Wizard
            </h2>
          </div>
          <p className="text-xl text-neutral-600">
            Quickly identify and compare the right platforms and CROs for your research needs
          </p>
        </div>

        {/* Step Content */}
        <div className="mb-12">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 border ${
              currentStep === 1
                ? 'bg-neutral-50 text-neutral-400 cursor-not-allowed border-neutral-200'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 border-neutral-300'
            }`}
          >
            Back
          </button>

          <div className="flex gap-2">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  i + 1 === currentStep
                    ? 'bg-primary-600'
                    : i + 1 < currentStep
                    ? 'bg-primary-300'
                    : 'bg-neutral-200'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200"
          >
            {currentStep === totalSteps ? 'Get Recommendations' : 'Next'}
          </button>
        </div>
      </div>
    </section>
  );
}



export default function HomePage() {
  const { loading } = useAuth();
  
  // Search state - moved from SearchSection
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch data from all relevant tables
  const { data: crosData } = useDynamicAirtableRecords('CROS');
  const { data: providersData } = useDynamicAirtableRecords('PROVIDERS');
  const { data: platformsData } = useDynamicAirtableRecords('PLATFORMS');
  const { data: productsData } = useDynamicAirtableRecords('PRODUCTS');
  const { data: technologiesData } = useDynamicAirtableRecords('TECHNOLOGY');

  // Combine all data for search
  const allData = useMemo(() => {
    const combined = [];
    
    if (crosData && Array.isArray(crosData)) {
      combined.push(...crosData.map(item => ({ ...item, type: 'CRO', table: 'CROS' })));
    }
    if (providersData && Array.isArray(providersData)) {
      combined.push(...providersData.map(item => ({ ...item, type: 'Provider', table: 'PROVIDERS' })));
    }
    if (platformsData && Array.isArray(platformsData)) {
      combined.push(...platformsData.map(item => ({ ...item, type: 'Platform', table: 'PLATFORMS' })));
    }
    if (productsData && Array.isArray(productsData)) {
      combined.push(...productsData.map(item => ({ ...item, type: 'Product', table: 'PRODUCTS' })));
    }
    if (technologiesData && Array.isArray(technologiesData)) {
      combined.push(...technologiesData.map(item => ({ ...item, type: 'Technology', table: 'TECHNOLOGY' })));
    }
    
    return combined;
  }, [crosData, providersData, platformsData, productsData, technologiesData]);

  // Search function
  const performSearch = useMemo(() => {
    return (query: string, category: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      
      // Filter by category first
      let filteredData = allData;
      if (category !== 'all') {
        filteredData = allData.filter(item => item.type.toLowerCase() === category.toLowerCase());
      }

      // Search in relevant fields
      const results = filteredData.filter(item => {
        // Helper function to convert array fields to string
        const arrayToString = (field: any) => {
          if (Array.isArray(field)) {
            return field.join(' ');
          }
          return field || '';
        };

        const searchableFields = [
          // Common fields
          arrayToString(item['Provider'] || item['Name'] || item['Company'] || item['CRO'] || ''),
          arrayToString(item['Description'] || ''),
          arrayToString(item['Service Types'] || ''),
          arrayToString(item['Technologies'] || ''),
          arrayToString(item['Platforms'] || ''),
          arrayToString(item['Location'] || ''),
          arrayToString(item['Country'] || ''),
          
          // Platform-specific fields
          arrayToString(item['Platform Name'] || ''),
          arrayToString(item['Platform Description'] || ''),
          arrayToString(item['Provider'] || ''),
          arrayToString(item['Products'] || ''),
          arrayToString(item['Readout'] || ''),
          arrayToString(item['Plex Type'] || ''),
          arrayToString(item['Throughput'] || ''),
          arrayToString(item['Resolution'] || ''),
          arrayToString(item['Chemistry'] || ''),
          arrayToString(item['Technology'] || ''),
          arrayToString(item['CROs'] || ''),
          
          // Product-specific fields
          arrayToString(item['Product Name'] || ''),
          arrayToString(item['Instrument Type'] || ''),
          arrayToString(item['Platforms'] || ''),
          arrayToString(item['Technology'] || ''),
          arrayToString(item['Readout'] || ''),
          arrayToString(item['Application'] || ''),
          arrayToString(item['Modality'] || ''),
          arrayToString(item['Plex Type'] || ''),
          arrayToString(item['Resolution'] || ''),
          arrayToString(item['Markers (Maximum)'] || ''),
          arrayToString(item['Throughput'] || ''),
          arrayToString(item['Chemistry'] || ''),
          arrayToString(item['Compatible Assays'] || ''),
          arrayToString(item['CROs'] || ''),
          
          // Technology-specific fields
          arrayToString(item['Technology Name'] || ''),
          arrayToString(item['Technology Description'] || ''),
          
          // CRO-specific fields
          arrayToString(item['Therapeutic Areas'] || ''),
          arrayToString(item['Regulatory Compliance'] || ''),
          arrayToString(item['Instruments'] || ''),
          arrayToString(item['Software'] || ''),
          arrayToString(item['Partnered Companies'] || '')
        ].join(' ').toLowerCase();

        return searchableFields.includes(query.toLowerCase());
      });

      setSearchResults(results.slice(0, 10)); // Limit to 10 results
      setShowResults(true);
      setIsSearching(false);
    };
  }, [allData]);

  // Handle search input
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(searchQuery, selectedCategory);
  };

  // Handle category filter
  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
    if (searchQuery.trim()) {
      performSearch(searchQuery, category);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setSelectedCategory('all');
  };

  // Toggle filters
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingPattern patternName="cell-pattern" size="md" />
      </div>
    );
  }

  return (
    <>
      {/* Top Section Background */}
      <div className="absolute top-0 left-0 w-full h-[320px] bg-neutral-900 bg-gradient-to-b from-neutral-900 to-transparent z-0 pointer-events-none" />
      {/* Main Content */}
      <ClientOnly>
        <DirectoryProvider>
          {/* Hero Section - Replaced Carousel */}
          <div className="w-full relative z-10">
            <div className="w-full min-h-screen bg-gradient-to-r from-primary-500 to-accent-500 shadow-lg overflow-hidden relative rounded-none flex items-center justify-center">
              <div className="max-w-7xl mx-auto w-full py-16">
                {/* Title and Subtitle */}
                <div className="text-center mb-24">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <img 
                      src="/LabAtlas-Logo-White.png" 
                      alt="LabAtlas Logo" 
                      className="h-16 w-16 md:h-20 md:w-20 object-contain"
                    />
                    <h2 className="font-display text-5xl md:text-6xl font-extrabold text-white">LabAtlas</h2>
                  </div>
                  <div className="font-display text-2xl md:text-4xl font-light text-white">The Complete Map of Spatial-Biology Services</div>
                </div>
                
                {/* Search Section - Moved from separate section */}
                <div className="w-full max-w-4xl mx-auto mb-28">
        {/* Search Bar */}
                  <div className="w-full">
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        className="w-full px-6 py-4 text-lg border-2 border-white/20 rounded-lg focus:border-white focus:outline-none shadow-lg bg-white/90 backdrop-blur-sm text-neutral-900"
            />
            {/* Filter Button */}
            <button
              type="button"
              onClick={toggleFilters}
              className={`absolute right-24 top-1/2 transform -translate-y-1/2 transition-colors duration-200 flex items-center justify-center ${
                showFilters 
                  ? 'text-accent-600' 
                  : 'text-neutral-600 hover:text-accent-500'
              }`}
              aria-label={showFilters ? "Close filters" : "Open filters"}
              style={{ zIndex: 2 }}
            >
              <Filter size={20} />
            </button>
            {/* Search Button */}
            <button 
              type="submit"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md transition-colors duration-200"
              aria-label="Search"
              style={{ zIndex: 1 }}
            >
              {isSearching ? <LoadingPattern patternName="cell-pattern" size="sm" /> : <Search size={20} />}
            </button>
          </form>
          
          {/* Quick Search Options */}
          {showFilters && (
            <div className="flex flex-wrap justify-center gap-3 mt-6 animate-in slide-in-from-top-2 duration-200">
              {[
                { key: 'all', label: 'All' },
                { key: 'cro', label: 'CROs' },
                { key: 'provider', label: 'Providers' },
                { key: 'platform', label: 'Platforms' },
                { key: 'product', label: 'Products' },
                { key: 'technology', label: 'Technologies' }
              ].map((category) => (
                <button
                  key={category.key}
                  onClick={() => handleCategoryFilter(category.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    selectedCategory === category.key
                                ? 'bg-white text-primary-700'
                                : 'bg-white/20 border border-white/30 text-white hover:bg-white/30'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          )}

        {/* Search Results */}
        {showResults && (
                      <div className="mt-6 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-white/20">
                        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
                          <h4 className="font-display font-bold text-lg text-primary-700">
                  Search Results ({searchResults.length})
                          </h4>
                <button
                  onClick={clearSearch}
                            className="text-neutral-500 hover:text-neutral-700 transition-colors"
                            aria-label="Clear search"
                >
                  <X size={20} />
                </button>
              </div>
                        {searchResults.length > 0 ? (
                          <div className="p-4">
                            {searchResults.map((result, index) => {
                              // Helper function to get the proper name based on record type
                              const getName = (record: any) => {
                                // Check for linked record fields first (these might contain IDs)
                                if (record.type === 'CRO') {
                                  return record['CRO'] || record['Provider'] || record['Name'] || 'Unnamed CRO';
                                } else if (record.type === 'Provider') {
                                  return record['Company'] || record['Provider'] || record['Name'] || 'Unnamed Provider';
                                } else if (record.type === 'Platform') {
                                  return record['Platform Name'] || record['Name'] || 'Unnamed Platform';
                                } else if (record.type === 'Product') {
                                  return record['Product Name'] || record['Name'] || 'Unnamed Product';
                                } else if (record.type === 'Technology') {
                                  return record['Technology Name'] || record['Name'] || 'Unnamed Technology';
                                }
                                // Fallback to common fields
                                return record['Provider'] || record['Name'] || record['Company'] || record['CRO'] || record['Product Name'] || record['Technology Name'] || record['Platform Name'] || 'Unnamed';
                              };

                              // Helper function to get description based on record type
                              const getDescription = (record: any) => {
                                if (record.type === 'Platform') {
                                  return record['Platform Description'] || record['Description'] || '';
                                } else if (record.type === 'Product') {
                                  return record['Product Description'] || record['Description'] || '';
                                } else if (record.type === 'Technology') {
                                  return record['Technology Description'] || record['Description'] || '';
                                }
                                return record['Description'] || '';
                              };

                              const name = getName(result);
                              const description = getDescription(result);
                              const type = result.type;
                              const location = result['Location'] || result['Country'] || '';
                              
                              // Skip results that have ID-like names (likely Airtable record IDs)
                              if (name && (name.length > 20 || /^[a-zA-Z0-9]{20,}$/.test(name))) {
                                return null;
                              }
                    
                    return (
                                <div key={`${result.id}-${index}`} className="mb-4 p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors cursor-pointer">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="inline-block bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs font-medium">
                              {type}
                            </span>
                                        <h5 className="font-display font-bold text-lg text-primary-700">{name}</h5>
                          </div>
                          {description && (
                                        <p className="text-sm text-neutral-600 mb-2 line-clamp-2">{description}</p>
                          )}
                          {location && (
                                        <p className="text-xs text-neutral-500 italic">{location}</p>
                                      )}
                                    </div>
                        </div>
                      </div>
                    );
                            }).filter(Boolean)}
                          </div>
                        ) : (
                          <div className="p-8 text-center text-neutral-500">
                            <Search size={48} className="mx-auto mb-4 text-neutral-300" />
                            <p className="font-sans text-lg">No results found for "{searchQuery}"</p>
                            <p className="text-sm mt-2">Try adjusting your search terms or filters</p>
                </div>
              )}
          </div>
        )}
      </div>
                </div>
                
                {/* Three Feature Cards */}
                <div className="flex flex-col md:flex-row gap-8 w-full justify-center items-stretch">
                  {/* Step 1 */}
                  <div className="flex-1 flex flex-col items-center bg-white/90 backdrop-blur-sm rounded-lg p-8 shadow-lg border border-white/20 mb-4 md:mb-0">
                    <MapPinned className="mb-4 text-primary-500" size={48} strokeWidth={2.2} />
                    <div className="font-display font-bold text-2xl mb-3 text-center text-primary-700">Discover & Explore</div>
                    <div className="font-sans text-base text-neutral-700 text-center">Browse the complete directory of spatial-biology companies, platforms, and services.</div>
                  </div>
                  {/* Step 2 */}
                  <div className="flex-1 flex flex-col items-center bg-white/90 backdrop-blur-sm rounded-lg p-8 shadow-lg border border-white/20 mb-4 md:mb-0">
                    <Handshake className="mb-4 text-secondary-600" size={48} strokeWidth={2.2} />
                    <div className="font-display font-bold text-2xl mb-3 text-center text-primary-700">Engage & Connect</div>
                    <div className="font-sans text-base text-neutral-700 text-center">Reach out to CROs, request quotes, or update your own profile—all in one place.</div>
                  </div>
                  {/* Step 3 */}
                  <div className="flex-1 flex flex-col items-center bg-white/90 backdrop-blur-sm rounded-lg p-8 shadow-lg border border-white/20">
                    <LineChart className="mb-4 text-accent-600" size={48} strokeWidth={2.2} />
                    <div className="font-display font-bold text-2xl mb-3 text-center text-primary-700">Stay Informed</div>
                    <div className="font-sans text-base text-neutral-700 text-center">Track updates, manage vendors, and stay ahead with the latest in spatial-biology.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End Hero Section */}

          {/* Guided Research Wizard */}
          <GuidedResearchWizard />

          {/* Featured Platforms section */}
          <section className="w-full py-16 px-4 md:px-12 bg-neutral-50 rounded-none flex flex-col items-center justify-center">
            <div className="max-w-7xl w-full mx-auto flex flex-col items-start">
              <div className="flex items-center justify-between w-full mb-6">
                <div className="flex items-center gap-3">
                  <Star className="text-accent-600" size={32} />
                  <h2 className="font-display text-4xl font-bold text-left text-primary-700">Featured Platforms</h2>
                </div>
                <Link
                  href="/platforms"
                  className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  View All Platforms
                  <ExternalLink size={20} className="ml-2" />
                </Link>
              </div>
              <p className="font-sans text-xl text-neutral-700 text-left mb-12 w-full">
                Explore some of the leading platforms in spatial-biology services:
              </p>
              <FeaturedPlatforms />
            </div>
          </section>

          {/* Featured CROs section */}
          <section className="w-full py-16 px-4 md:px-12 bg-white rounded-none flex flex-col items-center justify-center">
            <div className="max-w-7xl w-full mx-auto flex flex-col items-start">
              <div className="flex items-center justify-between w-full mb-6">
                <div className="flex items-center gap-3">
                  <Star className="text-accent-600" size={32} />
                  <h2 className="font-display text-4xl font-bold text-left text-primary-700">Featured CROs</h2>
                </div>
                <Link
                  href="/cros"
                  className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  View All CROs
                  <ExternalLink size={20} className="ml-2" />
                </Link>
              </div>
              <p className="font-sans text-xl text-neutral-700 text-left mb-12 w-full">
                Discover some of the leading Contract Research Organizations in spatial-biology services:
              </p>
              <FeaturedCROs />
            </div>
          </section>

          {/* Who is LabAtlas for? section */}
          <section className="w-full py-16 px-4 md:px-12 bg-gradient-to-r from-neutral-50 via-neutral-100 to-neutral-50 rounded-none flex flex-col items-center justify-center">
            <div className="max-w-7xl w-full mx-auto flex flex-col items-start">
              <h2 className="font-display text-4xl font-bold text-left mb-6 text-primary-700">Who is LabAtlas for?</h2>
              <p className="font-sans text-2xl text-neutral-700 text-left mb-10 w-full">
                LabAtlas empowers researchers and labs to discover, engage, and connect with the right partners and platform CROs for every project:
              </p>
              <Tabs defaultValue="researcher" className="w-full">
                <TabsList className="mb-6 flex gap-6 p-2">
                  <TabsTrigger value="researcher" className="font-display flex items-center gap-3 px-8 py-4 min-w-[200px] justify-center">
                    <FlaskConical size={40} className="text-primary-500" /> Researchers
                  </TabsTrigger>
                  <TabsTrigger value="labmanager" className="font-display flex items-center gap-3 px-8 py-4 min-w-[200px] justify-center">
                    <ClipboardList size={40} className="text-secondary-600" /> Lab Managers
                  </TabsTrigger>
                  <TabsTrigger value="cro" className="font-display flex items-center gap-3 px-8 py-4 min-w-[200px] justify-center">
                    <Building2 size={40} className="text-accent-600" /> CRO Representatives
                  </TabsTrigger>
                </TabsList>
                {/* White divider line between tabs and content */}
                <div className="w-full border-t border-neutral-200 mb-6" />
                <TabsContent value="researcher">
                  <div className="p-6 md:p-8 flex flex-col items-start w-full">
                    <div className="mb-3">
                      <div className="font-display text-2xl font-extrabold text-primary-700 mb-1">Ready to find the perfect CRO for your next project?</div>
                      <div className="font-sans text-base text-neutral-700">LabAtlas lets you search, compare, and shortlist CROs in seconds. Request quotes, track your favorites, and make confident decisions—all in one place.</div>
                    </div>
                    <div className="font-sans font-semibold mb-2 text-neutral-900">With LabAtlas, you can:</div>
                    <ul className="font-sans text-base font-medium space-y-2 list-disc pl-6 text-neutral-700">
                      <li>Discover and compare CROs tailored to your research needs</li>
                      <li>Request quotes instantly—no more endless email chains</li>
                      <li>Shortlist and organize your top picks for easy review</li>
                    </ul>
                  </div>
                </TabsContent>
                <TabsContent value="labmanager">
                  <div className="p-6 md:p-8 flex flex-col items-start w-full">
                    <div className="mb-3">
                      <div className="font-display text-2xl font-extrabold text-primary-700 mb-1">Juggling budgets, timelines, and vendors?</div>
                      <div className="font-sans text-base text-neutral-700">LabAtlas is your command center. Coordinate quotes, manage all your service CROs, and keep your lab running smooth—no more spreadsheet chaos.</div>
                    </div>
                    <div className="font-sans font-semibold mb-2 text-neutral-900">With LabAtlas, you can:</div>
                    <ul className="font-sans text-base font-medium space-y-2 list-disc pl-6 text-neutral-700">
                      <li>Request and compare quotes from multiple vendors in one view</li>
                      <li>Track and manage all your lab's service CROs</li>
                      <li>Stay on top of timelines and never miss a beat</li>
                    </ul>
                  </div>
                </TabsContent>
                <TabsContent value="cro">
                  <div className="p-6 md:p-8 flex flex-col items-start w-full">
                    <div className="mb-3">
                      <div className="font-display text-2xl font-extrabold text-primary-700 mb-1">Want to stand out and grow your business?</div>
                      <div className="font-sans text-base text-neutral-700">LabAtlas puts your CRO in front of the right researchers and labs. Update your profile, track competitors, and manage new leads—all from your dashboard.</div>
                    </div>
                    <div className="font-sans font-semibold mb-2 text-neutral-900">With LabAtlas, you can:</div>
                    <ul className="font-sans text-base font-medium space-y-2 list-disc pl-6 text-neutral-700">
                      <li>Showcase your services to a targeted audience</li>
                      <li>Update your profile and keep your offerings fresh</li>
                      <li>Track competitors and manage leads in real time</li>
                    </ul>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </section>

          <footer className="w-full py-8 bg-neutral-900">
            <div className="max-w-7xl mx-auto px-4 text-center text-neutral-500 text-sm">
              © 2025 LabAtlas. All rights reserved.
            </div>
          </footer>
        </DirectoryProvider>
      </ClientOnly>
    </>
  );
}
